import React, { useMemo, useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Activity, Clock, Database } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Chart style constants - prevent re-creation on every render
const CHART_STYLES = {
  tooltip: {
    backgroundColor: 'hsl(var(--card))',
    border: '1px solid hsl(var(--border))',
    borderRadius: '8px',
  },
  xAxisTick: { fill: 'hsl(var(--muted-foreground))' },
  yAxisTick: { fill: 'hsl(var(--muted-foreground))' },
};

const BAR_RADIUS = [0, 8, 8, 0];

const Analytics = () => {
  const [metrics, setMetrics] = useState(null);
  const [providers, setProviders] = useState([]);
  const [geographic, setGeographic] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch analytics data
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const [metricsRes, providersRes, geoRes] = await Promise.all([
          fetch(`${API_URL}/api/analytics/metrics`),
          fetch(`${API_URL}/api/analytics/providers`),
          fetch(`${API_URL}/api/analytics/geographic`)
        ]);

        const metricsData = await metricsRes.json();
        const providersData = await providersRes.json();
        const geoData = await geoRes.json();

        setMetrics(metricsData);
        setProviders(providersData.providers || []);
        setGeographic(geoData.geographic || []);
      } catch (error) {
        // Error logging - production should use proper error service
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching analytics:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, []); // Empty deps - fetch once on mount

  // Calculate response mode distribution from metrics
  const responseModeData = useMemo(() => {
    if (!metrics) return [];
    
    const liveCount = Math.round(metrics.totalRequests * (1 - metrics.fallbackRate / 100));
    const fallbackCount = metrics.totalRequests - liveCount;
    
    return [
      { 
        name: 'Live Rates', 
        value: liveCount,
        color: 'hsl(var(--chart-1))' 
      },
      { 
        name: 'Zone Fallback', 
        value: Math.round(fallbackCount * 0.7),
        color: 'hsl(var(--chart-4))' 
      },
      { 
        name: 'Country Estimate', 
        value: Math.round(fallbackCount * 0.25),
        color: 'hsl(var(--chart-2))' 
      },
      { 
        name: 'Errors', 
        value: Math.round(fallbackCount * 0.05),
        color: 'hsl(var(--chart-5))' 
      },
    ].filter(item => item.value > 0);
  }, [metrics]);

  // Transform providers data for chart
  const providerData = useMemo(() => {
    return providers.map(p => ({
      provider: p.name,
      requests: p.requestsToday,
      success: Math.round(p.requestsToday * (p.successRate / 100))
    }));
  }, [providers]);

  // Transform geographic data for chart
  const geoData = useMemo(() => {
    return geographic.slice(0, 10);
  }, [geographic]);

  if (loading || !metrics) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-muted-foreground">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Analytics</h1>
        <p className="text-muted-foreground mt-1">
          Detailed insights and performance metrics
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Requests</p>
                <p className="text-2xl font-bold">{metrics.totalRequests.toLocaleString()}</p>
                <p className="text-xs text-success mt-1">Last 24 hours</p>
              </div>
              <Activity className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Response</p>
                <p className="text-2xl font-bold">{metrics.avgLatency}ms</p>
                <p className="text-xs text-success mt-1">Average latency</p>
              </div>
              <Clock className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Cache Efficiency</p>
                <p className="text-2xl font-bold">{metrics.cacheHitRate}%</p>
                <p className="text-xs text-success mt-1">Hit rate</p>
              </div>
              <Database className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Success Rate</p>
                <p className="text-2xl font-bold">{metrics.successRate}%</p>
                <p className="text-xs text-success mt-1">Successful requests</p>
              </div>
              <TrendingUp className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Response Mode Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Response Mode Distribution</CardTitle>
            <CardDescription>Breakdown by response type</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={responseModeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {responseModeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Provider Performance */}
        <Card>
          <CardHeader>
            <CardTitle>Provider Performance</CardTitle>
            <CardDescription>Request volume and success rates</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={providerData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis 
                  dataKey="provider" 
                  tick={CHART_STYLES.xAxisTick}
                />
                <YAxis 
                  tick={CHART_STYLES.yAxisTick}
                />
                <Tooltip contentStyle={CHART_STYLES.tooltip} />
                <Legend />
                <Bar dataKey="requests" fill="hsl(var(--chart-1))" name="Requests" />
                <Bar dataKey="success" fill="hsl(var(--chart-3))" name="Successful" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Geographic Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Geographic Distribution</CardTitle>
            <CardDescription>Requests by destination city</CardDescription>
          </CardHeader>
          <CardContent>
            {geoData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={geoData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis 
                    type="number"
                    tick={CHART_STYLES.xAxisTick}
                  />
                  <YAxis 
                    dataKey="city" 
                    type="category"
                    tick={CHART_STYLES.yAxisTick}
                  />
                  <Tooltip contentStyle={CHART_STYLES.tooltip} />
                  <Bar dataKey="count" fill="hsl(var(--chart-2))" radius={BAR_RADIUS} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No geographic data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Provider Performance Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Provider Summary</CardTitle>
            <CardDescription>Performance by provider</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {providers.slice(0, 5).map((provider) => (
                <div key={provider.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium">{provider.name}</div>
                    <div className="text-sm text-muted-foreground">
                      Success: {provider.successRate}% | Latency: {provider.avgLatency}ms
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold">{provider.requestsToday}</div>
                    <div className="text-xs text-muted-foreground">requests</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Metrics */}
      <Card>
        <CardHeader>
          <CardTitle>Performance Breakdown</CardTitle>
          <CardDescription>Key metrics summary</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-4 p-4 border border-border rounded-lg">
            <div>
              <p className="text-sm font-medium">Last 24 Hours</p>
              <p className="text-2xl font-bold mt-1">{metrics.totalRequests.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">requests</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Avg Latency</p>
              <p className="text-xl font-bold mt-1">{metrics.avgLatency}ms</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Cache Hit</p>
              <p className="text-xl font-bold mt-1">{metrics.cacheHitRate}%</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Success Rate</p>
              <p className="text-xl font-bold mt-1">{metrics.successRate}%</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Fallback Rate</p>
              <p className="text-xl font-bold mt-1">{metrics.fallbackRate}%</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Analytics;
